/**
 * S23. Traffic.
 *
 * Route /p/:slug/traffic, rail slot between Activity and Usage.
 *
 * THE PAGE ANSWERS ONE QUESTION TODAY: did anybody come. It was designed to
 * answer three, adding "did they get what they came for" and "was it quick",
 * and the copy and structure below still assume all three, because the other
 * two are a pipeline away rather than a redesign away. But `outcome`, `paths`
 * and `places` come back null for every project (services/obs/traffic.ts) and
 * per-bucket timings are thin, so those zones rendered four "we do not count
 * this yet" apologies in a row. They are not rendered; the note where they
 * used to be says what each one needs. Restore a zone in the same change that
 * gives it real data, never before.
 *
 * Overview answers "am I fine", Activity answers "what broke and how do I get
 * it fixed", Usage answers "how much of my allowance is gone".
 *
 * IT IS NOT CALLED HEALTH AND IT CARRIES NO LAMP. The health verdict already
 * exists, above the fold, on the most visited screen in the product, and it is
 * a pure precedence function with one lamp and one sentence. A second
 * authority on that question would invite the exact trip the overview exists
 * to prevent, and when the lamp is green a Health page has nothing to say
 * beyond "still green", which is the console that teaches people not to open
 * it. So this header says nothing about whether the app is fine: it says what
 * the last seven days looked like, and defers.
 *
 * The component named TrafficLight owns the health verdict and keeps its name.
 * That collision lives in the codebase and never on screen: the lamp's own
 * aria-label is "Whether your app is fine", and the only user facing use of
 * the word today is "No traffic yet". Do not rename either one.
 *
 * EVERY VISUAL HERE IS A SENTENCE WITH A PICTURE UNDER IT, never a picture
 * with a caption. Delete every chart on this page and each section still says
 * what it says. That is the discipline the usage bar already keeps ("the bar
 * is decoration, the number is the content").
 *
 * THE TWO SENTENCES WORTH THE WHOLE SCREEN, and neither one is a number:
 *   "A visit is one request your app answered. One person usually makes
 *    several." Every competitor causes that misread and none of them corrects
 *    it.
 *   "This is how long your app took to answer, not how long the page took to
 *    appear in the visitor's browser." That is also the honest boundary of
 *    what the platform can see without putting a script in somebody else's
 *    visitor's browser, which is a product decision and not a metric toggle.
 *
 * A 404 IS NOT AN ERROR. "Your app broke" and "somebody asked for something
 * that is not there" are two different sentences, and one error rate melts
 * them together. The first two segments of the outcome track are neutral ink
 * at two weights, because a stranger probing for an admin page is an ordinary
 * outcome and not a state. Only the error segment spends a state token. (That
 * track is one of the zones currently not rendered; the rule survives it.)
 *
 * VISITS IS A LINE, AND IT IS DRAWN EVEN WHEN IT IS ALL ZEROS. The series is
 * dense and ends at now, because the backend zero-fills every bucket in the
 * window, so a quiet week is seven points on the floor rather than two ragged
 * columns and a right-hand edge sitting on whichever day last had traffic. A
 * column drew nothing at all for a zero, which reads as "no data", and the
 * true sentence is "nobody came". See the Line component.
 *
 * ONE INK BLOCK, AND NEVER TWO. Zero visits spends it on "Visit your app".
 * Errors spend it on "See the errors", which goes to the page that owns them.
 * Those two cannot both be true: no visits means no errors. This screen never
 * renders an ErrorBlock and never renders a Copy fix request button, because
 * Activity owns those, and that is how the two screens keep one ink block
 * between them.
 *
 * NOTHING ON THIS PAGE MOVES. No entrance walk on the columns, no loop, no
 * transition. The doctrine allows one moving thing per screen and this screen
 * spends zero, so the reduced motion blanket in base.css has nothing to still.
 *
 * SIMPLE VERSUS ADVANCED. Both modes get the window picker (24 hours, 7 days,
 * 30 days) and open on seven days; the picker used to be Advanced only, which
 * hid the most asked-for control on the page. Advanced appends inside each section, above nothing and
 * replacing nothing, in the idiom Activity already set: an ADVANCED chip, then
 * the extra layer. The one AdvancedDisclosure on the page wraps the "what we
 * keep" plate at the bottom, which is the block a Simple reader may genuinely
 * want to open once.
 *
 * NO DAY BY DAY TABLE HERE. Usage Advanced already prints one, with requests
 * per day. Two screens printing the same table is how a person ends up
 * comparing two numbers that were meant to be one. Traffic Advanced adds
 * resolution and comparison; Usage Advanced keeps the ledger.
 *
 * FAKEABLE STATES, deep linkable per prototype mechanic (a):
 *   ?state=quiet          measured, and nobody came
 *   ?state=not-measured   visits counted, the rest of the pipeline never ran
 *   ?state=slow           one genuinely slow day, for the attention dot
 *   ?state=loading        the skeletons, held open
 */

import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '../../components/primitives/Button';

import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { SegmentedControl } from '../../components/patterns/SegmentedControl';
import { Skeleton } from '../../components/patterns/Skeleton';

import { environmentOf, getProject, getTraffic } from '../../lib/api';
import { useProjects } from '../../lib/auth/use-app-data';
import { count } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { getLang, t } from '../../lib/i18n/t';
import { TRAFFIC_WINDOWS } from '../../lib/mock/seed';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import { RequestsChart } from '../../components/patterns/RequestsChart';
import type { Traffic, TrafficWindow } from '../../lib/types';

import '../../styles/screens/observability.css';

// 'not-measured' and 'slow' now change nothing a reader can see: the zones
// they were written for (outcome, speed, paths, places) are not rendered
// while the pipeline produces no real values for them. They are kept, not
// deleted, because `force` is the demo apparatus's only lever on this page
// and the states come back with the zones.
const STATES = ['resting', 'quiet', 'not-measured', 'slow', 'loading'] as const;
type StateName = (typeof STATES)[number];

/** The window the page opens on, in both modes. */
const DEFAULT_WINDOW: TrafficWindow = '7d';

/** What the forced slow day is set to, so the attention dot is reachable. */
const FORCED_SLOW_MS = 940;


/** Visits and errors are a daily rollup of six numbers, cheap to keep. */
const KEPT_MONTHS = 3;

/* ------------------------------------------------------------- the words */

/**
 * Every sentence on this page changes with the window, because the sentences
 * are the content. They are written out per window rather than assembled from
 * parts: "1 visits" is the tell that a screen is gluing strings together, and
 * this is a screen whose whole job is to be believed.
 *
 * They are functions rather than plain strings for one mechanical reason: a
 * table of bare keys read as t(TABLE[window]) puts thirty sentences somewhere
 * a catalogue sweep for t('...') will never find them. Wrapping each one in
 * its own call keeps the whole page collectable from source, which is the
 * promise t.ts makes at the top of its own file.
 */

const WINDOW_LINE: Record<TrafficWindow, () => string> = {
  '24h': () => t('The last 24 hours.'),
  '7d': () => t('The last 7 days.'),
  '30d': () => t('The last 30 days.'),
};

const WINDOW_PICK: Record<TrafficWindow, () => string> = {
  '24h': () => t('Last 24 hours'),
  '7d': () => t('Last 7 days'),
  '30d': () => t('Last 30 days'),
};

interface VisitsSay {
  one: () => string;
  many: (n: string) => string;
  /** Above the sampling threshold the exact number stops being offered. */
  about: (n: string) => string;
}

const VISITS_SAY: Record<TrafficWindow, VisitsSay> = {
  '24h': {
    one: () => t('Your app answered 1 request in the last 24 hours.'),
    many: (n) => t('Your app answered {n} requests in the last 24 hours.', { n }),
    about: (n) => t('Your app answered about {n} requests in the last 24 hours.', { n }),
  },
  '7d': {
    one: () => t('Your app answered 1 request in the last 7 days.'),
    many: (n) => t('Your app answered {n} requests in the last 7 days.', { n }),
    about: (n) => t('Your app answered about {n} requests in the last 7 days.', { n }),
  },
  '30d': {
    one: () => t('Your app answered 1 request in the last 30 days.'),
    many: (n) => t('Your app answered {n} requests in the last 30 days.', { n }),
    about: (n) => t('Your app answered about {n} requests in the last 30 days.', { n }),
  },
};

interface Direction {
  more: () => string;
  same: () => string;
  fewer: () => string;
  /** No before to compare with. "Up from 0" is a compliment nobody earned. */
  tooNew: () => string;
}

const DIRECTION: Record<TrafficWindow, Direction> = {
  '24h': {
    more: () => t('That is more than the 24 hours before.'),
    same: () => t('That is about the same as the 24 hours before.'),
    fewer: () => t('That is fewer than the 24 hours before.'),
    tooNew: () => t('Your app is too new to compare with the day before.'),
  },
  '7d': {
    more: () => t('That is more than the 7 days before.'),
    same: () => t('That is about the same as the 7 days before.'),
    fewer: () => t('That is fewer than the 7 days before.'),
    tooNew: () => t('Your app is too new to compare with the week before.'),
  },
  '30d': {
    more: () => t('That is more than the 30 days before.'),
    same: () => t('That is about the same as the 30 days before.'),
    fewer: () => t('That is fewer than the 30 days before.'),
    tooNew: () => t('Your app is too new to compare with the month before.'),
  },
};

const BEFORE_SAY: Record<TrafficWindow, (n: string) => string> = {
  '24h': (n) => t('The 24 hours before: {n} requests', { n }),
  '7d': (n) => t('The 7 days before: {n} requests', { n }),
  '30d': (n) => t('The 30 days before: {n} requests', { n }),
};

const BUCKET_SAY: Record<TrafficWindow, () => string> = {
  '24h': () => t('Each point is one hour.'),
  '7d': () => t('Each point is one day.'),
  '30d': () => t('Each point is one day.'),
};

/* ------------------------------------------------------------- helpers */

function locale(): string {
  return getLang() === 'th' ? 'th-TH' : 'en-GB';
}

/**
 * An estimate, rounded to two figures.
 *
 * An estimate printed to the unit is a precise lie. Above the sampling
 * threshold the exact number stops being offered anywhere in Simple, and this
 * is what takes its place.
 */
function about(value: number): number {
  if (value < 1000) return value;
  const step = 10 ** (Math.floor(Math.log10(value)) - 1);
  return Math.round(value / step) * step;
}

/**
 * The forced states. Every one of them moves the DATA and lets the sentences
 * follow, rather than setting a tone directly. A state that painted a slow day
 * without changing the number would be a screenshot of a lie.
 */
function force(traffic: Traffic, state: StateName): Traffic {
  if (state === 'quiet') {
    // Measured and empty: "we looked and there was nothing". Every family
    // answers, and every answer is a zero or an empty list.
    return {
      ...traffic,
      visits: 0,
      previousVisits: null,
      estimated: false,
      series: traffic.series.map((point) => ({ ...point, visits: 0, errors: 0 })),
      outcome: { answered: 0, sentElsewhere: 0, notFound: 0, errored: 0, crashed: 0, leftEarly: 0 },
      speed: null,
      paths: { keptForDays: traffic.paths?.keptForDays ?? 3, rows: [] },
      places: { keptForDays: traffic.places?.keptForDays ?? 3, rows: [] },
    };
  }

  if (state === 'not-measured') {
    // Not measured is "we do not know yet", and it is NOT a quiet app. These
    // two look identical in every other product on the market and they are not
    // the same statement at all, which is the entire reason the null rule
    // earns its keep.
    //
    // This branch used to zero the visits and the series along with the rest,
    // which quietly made the state unreachable: with no visits the speed zone
    // fell to "Nothing arrived, so there was nothing to time", which is the
    // sentence for an app nobody visited, not for an app whose response
    // reading pipeline has never run. Visits are the cheap family and they are
    // already counting, so the series stays exactly as it is and only the four
    // families that need the pipeline go null. That is the staged variant the
    // contract documents, and Soi Cats serves it without a state parameter.
    return { ...traffic, outcome: null, speed: null, paths: null, places: null };
  }

  if (state === 'slow' && traffic.speed) {
    const byDay = [...traffic.speed.byDay];
    // The middle of the window, so the dot row reads as one bad day inside a
    // good week rather than as a chart that has just started going wrong.
    const at = Math.max(0, Math.floor((byDay.length - 1) / 2));
    if (byDay[at] !== null) byDay[at] = FORCED_SLOW_MS;
    return { ...traffic, speed: { ...traffic.speed, slowestTenthMs: FORCED_SLOW_MS, byDay } };
  }

  return traffic;
}


/* -------------------------------------------------------------- the chip */

function AdvHead({ say }: { say: string }) {
  return (
    <div className="tf__adv-head">
      <span className="tf__chip">{t('ADVANCED')}</span>
      <p className="tf__adv-say">{say}</p>
    </div>
  );
}

/* ------------------------------------------------------------- the chrome */

interface HeadProps {
  /** The window every sentence on the page is written against. */
  span: TrafficWindow;
  /** The web address, once the project read has come back with one. */
  address?: string;
  /** The picker is shown wherever a window can be picked. */
  picker: boolean;
  picked: TrafficWindow;
  onPick: (window: TrafficWindow) => void;
  lineId: string;
}

/**
 * The header, and the only place the window picker is rendered.
 *
 * IT IS ONE COMPONENT BECAUSE THE PICKER HAS TO SURVIVE ITS OWN USE. useAsync
 * raises isLoading on every change to its deps, the window is one of those
 * deps, and this screen used to answer that by returning a whole different
 * page while the read was in flight. So the control a person had just touched
 * was torn out from under their hand and put back a moment later, focus and
 * all, every single time they changed the window. That is the one interaction
 * on this screen anybody performs twice, and it was the one that felt broken.
 *
 * Every branch below renders this same header in the same position, so React
 * keeps it mounted and only the DATA REGION underneath it changes: the zones
 * when there is an answer, the house Skeleton where they go when there is not
 * yet. Deploys does the same thing by repeating its page__head in each branch,
 * which is enough when the header is a title. This one holds a control that a
 * person is mid interaction with, so the repeat is a component rather than
 * three copies that could drift. The contract asks for it out loud: the window
 * picker stays live during the load.
 */
function Head({ span, address, picker, picked, onPick, lineId }: HeadProps) {
  return (
    <header className="page__head">
      <h1 className="page__title">{t('Traffic')}</h1>
      {address !== undefined ? (
        <p className="page__lede">{t('The requests your app answered at {address}.', { address })}</p>
      ) : null}
      <p className="tf__win" id={lineId}>
        {WINDOW_LINE[span]()}
      </p>

      {/* The picker sits under the lede in both modes. Changing the window
          rewrites every sentence on the page, which is correct: the
          sentences are the content. */}
      {picker ? (
        <div className="tf__head-adv">
          <SegmentedControl<TrafficWindow>
            label={t('How far back')}
            value={picked}
            onChange={onPick}
            describedBy={lineId}
            options={TRAFFIC_WINDOWS.map((one) => ({ value: one, label: WINDOW_PICK[one]() }))}
          />
        </div>
      ) : null}
    </header>
  );
}

/* ------------------------------------------------------------- the screen */

export default function Observability() {
  const slug = useProjectSlug() ?? '';
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const { state } = useScreenState<StateName>(STATES, 'resting');
  const [advanced] = useAdvanced();
  const windowLineId = useId();

  const [picked, setPicked] = useState<TrafficWindow>(DEFAULT_WINDOW);
  const span = picked;

  const cachedProjects = useProjects();
  const cachedProject = slug ? cachedProjects.find((p) => p.slug === slug) : undefined;
  const { data: fetchedProject, isLoading: isProjectLoading, error: projectError } = useAsync(
    () => (slug ? getProject(slug) : Promise.resolve(null)),
    [slug, scenarioId],
  );
  const project = fetchedProject ?? cachedProject;
  const projectId = project?.id ?? '';

  const { data: trafficData, error: trafficError, refetch } = useAsync(
    () => (projectId ? getTraffic(projectId, span) : Promise.resolve(undefined)),
    [projectId, span, scenarioId],
  );

  // The chrome is painted from whatever is already in hand. On the first paint
  // that is cached project and the header paints without blinking.
  const headAddress = project ? environmentOf(project).webAddress : undefined;

  useEffect(() => {
    if (project && trafficData) {
      const traffic = force(trafficData, state);
      captureEvent('traffic_screen_viewed', {
        project_slug: project.slug,
        window: span,
        visits: traffic.visits,
        has_errors: traffic.series.some((p) => p.errors > 0),
      });
    }
  }, [project, trafficData, span, state]);

  if ((isProjectLoading && !project) || !trafficData || state === 'loading') {
    return (
      <div className="page">
        <Head
          span={span}
          address={headAddress}
          picker
          picked={picked}
          onPick={setPicked}
          lineId={windowLineId}
        />
        <Skeleton shape="panel" count={2} label={t('Loading your traffic')} />
      </div>
    );
  }

  // A read that failed and a project that is not there are two different
  // sentences, and this screen used to print the second one for both. A throw
  // leaves data undefined, which fell straight into the not-found branch and
  // told somebody their app had been deleted because a request timed out. The
  // picker stays mounted here too: picking another window is the one recovery
  // this page can offer, and it reruns the read on its own.
  if (projectError || trafficError) {
    return (
      <div className="page">
        <Head
          span={span}
          address={headAddress}
          picker
          picked={picked}
          onPick={setPicked}
          lineId={windowLineId}
        />
        <Notice
          tone="attention"
          title={t('We could not load your traffic.')}
          actions={
            <Button variant="outline" onClick={refetch}>
              {t('Try again')}
            </Button>
          }
        >
          <p>{t('Your app is not affected. This is the page, not the app.')}</p>
        </Notice>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page">
        <Head
          span={span}
          picker={false}
          picked={picked}
          onPick={setPicked}
          lineId={windowLineId}
        />
        <Notice tone="quiet" title={t('That project is not here.')}>
          <p>{t('It may have been deleted, or the address may have a typo in it.')}</p>
        </Notice>
        <p>
          <Link className="link-inline" to="/projects">
            {t('All projects')}
          </Link>
        </p>
      </div>
    );
  }

  const traffic = force(trafficData, state);
  const base = `/p/${encodeURIComponent(project.slug)}`;
  const address = environmentOf(project).webAddress;
  const paused = project.status === 'paused';

  // `outcome`, `speed`, `paths` and `places` are on the wire but not read:
  // the zones that rendered them are not built out while the tail pipeline
  // returns null for every one of them. See the note in the JSX below.
  const { series, marks, estimated } = traffic;
  const visits = traffic.visits;
  const noVisits = visits <= 0;

  /* ---- zone 1, the sentences ---- */

  const visitsSay = estimated
    ? VISITS_SAY[span].about(count(about(visits)))
    : visits === 1
      ? VISITS_SAY[span].one()
      : VISITS_SAY[span].many(count(visits));

  const before = traffic.previousVisits;
  const directionSay =
    before === null
      ? DIRECTION[span].tooNew()
      : visits > before * 1.1
        ? DIRECTION[span].more()
        : visits < before * 0.9
          ? DIRECTION[span].fewer()
          : DIRECTION[span].same();

  const errorTotal = series.reduce((sum, point) => sum + point.errors, 0);
  const chartPoints = series.map((point) => ({
    at: point.at,
    requests: point.visits,
    errors: point.errors,
  }));
  const anyFailed = marks.some((mark) => mark.failed);
  // Errors join the chart in Advanced, and only when there were some: a flat
  // amber line at zero is a second series saying nothing.
  const showErrors = advanced && errorTotal > 0;
  const chartWords = {
    requests: t('requests'),
    errors: t('errors'),
    soFar: t('so far'),
    deploy: (version: string, time: string) =>
      t('Deployed {version} at {time}', { version, time }),
    deployFailed: (version: string, time: string) =>
      t('Deploy {version} failed at {time}', { version, time }),
  };

  /* ---- zones 2 to 5 ----

     Nothing is derived for them, because nothing renders them: see the note
     beside their old position in the JSX below. The wire fields they read
     (`outcome`, `paths`, `places`) are still destructured above so the
     shapes stay in the reader's view, and `speed` still feeds the retention
     plate's honesty line. */

  function visitApp() {
    window.open(address, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="page">
      <Head
        span={span}
        address={address}
        picker
        picked={picked}
        onPick={setPicked}
        lineId={windowLineId}
      />

      {/* ------------------------------------------------- zone 1. Visits */}

      {/* THE CHART IS DRAWN EVEN AT ZERO, which is the one change worth
          explaining here. The empty state used to replace it, so a person
          whose app had had no visitors saw no chart at all and could not
          tell "nobody came this week" from "we are not counting yet". The
          series is dense and ends at now (the backend zero-fills every
          bucket, services/obs/traffic.ts), so a quiet week draws seven real
          zeros along the floor and says the first thing. The empty state's
          sentence and its one action survive, above the chart rather than
          instead of it. */}
      <SectionPanel title={t('Requests')}>
        <div className="tf__zone">
          {noVisits ? (
            <>
              <p className="tf__say">{t('No requests yet.')}</p>
              <p className="tf__then">
                {paused
                  ? t('Your app is paused, so nothing is reaching it.')
                  : t('Your app is running. Nothing is wrong.')}
              </p>
            </>
          ) : (
            <>
              <p className="tf__say">{visitsSay}</p>
              <p className="tf__then">{directionSay}</p>
              {estimated ? (
                <p className="tf__quiet">{t('These are estimates, not exact counts.')}</p>
              ) : null}
            </>
          )}

          <RequestsChart
            points={chartPoints}
            marks={marks}
            bucket={span === '24h' ? 'hour' : 'day'}
            showErrors={showErrors}
            locale={locale()}
            label={
              span === '24h'
                ? t('Requests, one point per hour')
                : t('Requests, one point per day')
            }
            words={chartWords}
            onFirstHover={(input) =>
              captureEvent('traffic_chart_hovered', {
                project_slug: project.slug,
                window: span,
                advanced,
                input,
                deploys_in_window: marks.length,
              })
            }
            onFirstDeployFocus={() =>
              captureEvent('traffic_chart_deploy_highlighted', {
                project_slug: project.slug,
                window: span,
                deploys_in_window: marks.length,
              })
            }
          />

          {/* A legend only when there is more than the one line to tell apart. */}
          {showErrors || marks.length > 0 ? (
            <div className="rc-legend" aria-hidden="true">
              <span className="rc-legend__item">
                <span className="rc__key" data-tone="ink" />
                {t('Requests')}
              </span>
              {showErrors ? (
                <span className="rc-legend__item">
                  <span className="rc__key" data-tone="error" />
                  {t('Errors')}
                </span>
              ) : null}
              {marks.length > 0 ? (
                <span className="rc-legend__item">
                  <span className="rc__key" data-tone="deploy" />
                  {t('Deploy, tagged with its version')}
                </span>
              ) : null}
              {anyFailed ? (
                <span className="rc-legend__item">
                  <span className="rc__key" data-tone="deploy-failed" />
                  {t('Deploy that failed')}
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Hover on a pointer device, hold and drag on a touch screen. CSS
              picks the sentence, so it is right before any touch lands. */}
          <p className="tf__quiet">
            <span className="rc-hint" data-input="pointer">
              {t('Point at the chart to see the numbers. Point at a deploy line to pick it out.')}
            </span>
            <span className="rc-hint" data-input="touch">
              {t('Hold the chart and drag to see the numbers. Drag onto a deploy line to pick it out.')}
            </span>
          </p>

          {/* The misread every competitor causes: a request is not a person. */}
          <p className="tf__quiet">
            {t('A request is one thing a browser or program asked your app for. One page view usually makes several.')}
          </p>

          {noVisits && !paused ? (
            <div className="tf__zero-act">
              <Button variant="solid" onClick={visitApp}>
                {t('Visit your app')}
              </Button>
            </div>
          ) : null}
        </div>

        {advanced && !noVisits ? (
          <div className="tf__adv">
            <AdvHead
              say={
                errorTotal > 0
                  ? t('Errors are drawn on the chart too, on the same scale, because an error is a request.')
                  : t('No errors in this window.')
              }
            />
            <p className="tf__quiet">{BUCKET_SAY[span]()}</p>

            <p className="tf__totals mono">
              {t('{requests} requests, {errors} errors', {
                requests: count(visits),
                errors: count(errorTotal),
              })}
            </p>
            <p className="tf__totals mono">
              {before === null
                ? t('There is no earlier stretch to compare with.')
                : BEFORE_SAY[span](count(before))}
            </p>

            {/* A link, not a table. Usage Advanced keeps the ledger. */}
            <p>
              <Link className="link-inline" to={`${base}/usage`}>
                {t('See it against your plan limits')}
              </Link>
            </p>
          </div>
        ) : null}
      </SectionPanel>

      {/* ZONES 2 TO 5 ARE NOT RENDERED, and this is a capability statement
          rather than a layout preference. "What visitors got", "How fast it
          answered", "What people opened" and "Where visits came from" all
          read `outcome`, `speed.byDay`, `paths` and `places`, and the tail
          pipeline produces none of them today: cloud-api returns `outcome:
          null`, `paths: null` and `places: null` unconditionally
          (services/obs/traffic.ts). Each zone had a written "we do not count
          this for this app yet" branch, and four of those in a row is a page
          that is mostly apology. Visits is the one thing this page can
          actually answer, so it is the whole page until the pipeline can
          answer more. The JSX for all four is in git history at this commit;
          bring a zone back in the same change that gives it real data.

          `speed` is the one to watch when restoring: it is NOT null on the
          wire (the summary carries p50/p95), it is the per-bucket `byDay`
          array that is thin. Zone 3 is therefore the cheapest to bring back
          and should be the first. */}

      {/* ------------------------------------------- zone 6. What we count */}

      {/* Always visible, flat, no numbers. The traffic equivalent of the usage
          screen's policy block, and the strongest single differentiator this
          page has against every competitor. */}
      <section className="tf__policy" aria-label={t('What we count')}>
        <h2 className="tf__policy-title">{t('What we count')}</h2>
        <p className="tf__policy-say">
          {t(
            'We count the requests your app answers at your web address. We do not know who your visitors are, and we do not put anything in their browser.',
          )}
        </p>
        <p className="tf__policy-say">
          {t('So we can tell you how many requests there were, not how many people.')}
        </p>
      </section>

      <AdvancedDisclosure label={t('Show what we keep, and for how long')}>
        <dl className="tf__plate">
          <div className="tf__plate-row">
            <dt className="tf__plate-key">{t('Requests and errors')}</dt>
            <dd className="tf__plate-value">{t('Kept for {n} months', { n: KEPT_MONTHS })}</dd>
          </div>
          {/* Pages and places are not counted for any app yet and the zones
              that would show them are not rendered, so the plate does not
              quote a retention for data that does not exist. The row comes
              back with those zones. */}
          <div className="tf__plate-row">
            <dt className="tf__plate-key">{t('Counted at')}</dt>
            <dd className="tf__plate-value mono">{address}</dd>
          </div>
          <div className="tf__plate-row">
            <dt className="tf__plate-key">{t('How we count')}</dt>
            <dd className="tf__plate-value">
              {estimated
                ? t('Numbers are estimates, counted from a sample and multiplied up')
                : t('Numbers are exact')}
            </dd>
          </div>
          <div className="tf__plate-row">
            <dt className="tf__plate-key">{t('What happened at one moment')}</dt>
            <dd className="tf__plate-value">
              {/* The second door to the reserved screen. Activity Advanced has
                  the first. Traffic is where a person asks "what actually
                  happened at 14:05", and the tail is the only answer to that
                  question. Two doors to one reserved screen is correct here;
                  two copies of one table is not. */}
              <Link className="link-inline" to={`${base}/logs`}>
                {t('Watch the live logs')}
              </Link>
            </dd>
          </div>
        </dl>
      </AdvancedDisclosure>
    </div>
  );
}
